import { useCallback, useEffect, useState } from "react";
import DashboardHeader from "@/components/DashboardHeader";
import BackButton from "@/components/BackButton";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Separator } from "@/components/ui/separator";
import {
  adminGetPaymentSettings,
  adminTestPaymentConnection,
  adminUpdatePaymentSettings,
} from "@/lib/payments";
import {
  adminGetStudyPackDefaults,
  adminPutStudyPackDefaults,
} from "@/lib/studyPack";
import ReferralDefaultsCard from "@/components/ReferralDefaultsCard";
import { toast } from "sonner";
import {
  CheckCircle2,
  CreditCard,
  Eye,
  EyeOff,
  Loader2,
  Lock,
  ShieldCheck,
  XCircle,
} from "lucide-react";

function MaskedField({ label, value, placeholder, dataTestId }) {
  return (
    <div className="space-y-1">
      <Label className="text-xs text-stone-500">{label}</Label>
      <div
        className="px-3 py-2 rounded-md border border-stone-200 bg-stone-50 text-sm font-mono text-stone-700"
        data-testid={dataTestId}
      >
        {value || (
          <span className="text-stone-400 italic font-sans">{placeholder}</span>
        )}
      </div>
    </div>
  );
}

function SecretInput({ label, value, onChange, placeholder, dataTestId }) {
  const [show, setShow] = useState(false);
  return (
    <div className="space-y-1">
      <Label>{label}</Label>
      <div className="relative">
        <Input
          type={show ? "text" : "password"}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          className="pr-9 font-mono"
          data-testid={dataTestId}
        />
        <button
          type="button"
          onClick={() => setShow((s) => !s)}
          className="absolute right-2 top-1/2 -translate-y-1/2 text-stone-500"
          aria-label={show ? "Hide" : "Show"}
        >
          {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
        </button>
      </div>
    </div>
  );
}

function ProviderCard({
  provider,
  title,
  description,
  masked,
  onSave,
  onTest,
  onToggleEnabled,
}) {
  // Stripe vs Razorpay field names
  const isStripe = provider === "stripe";
  const [secret, setSecret] = useState("");
  const [pubOrKey, setPubOrKey] = useState("");
  const [whSecret, setWhSecret] = useState("");
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);

  useEffect(() => {
    setPubOrKey(
      isStripe ? masked?.publishable_key || "" : masked?.key_id || ""
    );
  }, [masked, isStripe]);

  const submit = async () => {
    setSaving(true);
    try {
      const body = isStripe
        ? {
            stripe: {
              secret_key: secret || undefined,
              publishable_key: pubOrKey || undefined,
              webhook_secret: whSecret || undefined,
            },
          }
        : {
            razorpay: {
              key_secret: secret || undefined,
              key_id: pubOrKey || undefined,
              webhook_secret: whSecret || undefined,
            },
          };
      await onSave(body);
      toast.success(`${title} keys saved`);
      setSecret("");
      setWhSecret("");
    } catch (e) {
      toast.error(
        e?.response?.data?.detail || `Failed to save ${title} settings`
      );
    } finally {
      setSaving(false);
    }
  };

  const test = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const r = await onTest();
      setTestResult(r);
    } catch (e) {
      setTestResult({
        ok: false,
        message: e?.response?.data?.detail || "Test failed",
      });
    } finally {
      setTesting(false);
    }
  };

  return (
    <Card className="border-stone-200" data-testid={`payment-card-${provider}`}>
      <CardHeader className="flex flex-row items-start justify-between space-y-0">
        <div>
          <CardTitle className="text-base flex items-center gap-2">
            <CreditCard className="h-4 w-4 text-amber-600" /> {title}
            {masked?.configured ? (
              <Badge
                variant="outline"
                className="bg-emerald-50 text-emerald-800 border-emerald-300"
                data-testid={`status-configured-${provider}`}
              >
                Configured
              </Badge>
            ) : (
              <Badge
                variant="outline"
                className="bg-stone-100 text-stone-600 border-stone-300"
                data-testid={`status-empty-${provider}`}
              >
                Not configured
              </Badge>
            )}
          </CardTitle>
          <p className="text-xs text-stone-500 mt-1">{description}</p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <span className="text-xs text-stone-500">
            {masked?.enabled ? "Live" : "Disabled"}
          </span>
          <Switch
            checked={!!masked?.enabled}
            disabled={!masked?.configured}
            onCheckedChange={(v) => onToggleEnabled(v)}
            data-testid={`toggle-enabled-${provider}`}
          />
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <MaskedField
            label={isStripe ? "Publishable key" : "Key ID"}
            value={
              isStripe ? masked?.publishable_key : masked?.key_id
            }
            placeholder={
              isStripe ? "pk_test_…" : "rzp_test_…"
            }
            dataTestId={`current-pub-${provider}`}
          />
          <MaskedField
            label={isStripe ? "Secret key" : "Key Secret"}
            value={masked?.secret_key_last4 || masked?.key_secret_last4}
            placeholder="(none set)"
            dataTestId={`current-secret-${provider}`}
          />
          <MaskedField
            label="Webhook secret"
            value={masked?.webhook_secret_last4}
            placeholder="(none set)"
            dataTestId={`current-whsec-${provider}`}
          />
        </div>

        <Separator />

        <div className="space-y-3">
          <div className="text-xs text-stone-600 uppercase tracking-wide flex items-center gap-1">
            <Lock className="h-3 w-3" /> Update keys (encrypted at rest)
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div className="space-y-1">
              <Label>{isStripe ? "Publishable key" : "Key ID"}</Label>
              <Input
                value={pubOrKey}
                onChange={(e) => setPubOrKey(e.target.value)}
                placeholder={isStripe ? "pk_test_…" : "rzp_test_…"}
                className="font-mono"
                data-testid={`input-pub-${provider}`}
              />
            </div>
            <SecretInput
              label={isStripe ? "Secret key" : "Key Secret"}
              value={secret}
              onChange={setSecret}
              placeholder={isStripe ? "sk_test_…" : "Secret value"}
              dataTestId={`input-secret-${provider}`}
            />
            <SecretInput
              label="Webhook secret"
              value={whSecret}
              onChange={setWhSecret}
              placeholder={isStripe ? "whsec_…" : "Razorpay webhook secret"}
              dataTestId={`input-whsec-${provider}`}
            />
          </div>

          <div className="flex flex-wrap items-center gap-2 pt-1">
            <Button
              onClick={submit}
              disabled={saving}
              className="bg-stone-900 hover:bg-stone-800 text-white"
              data-testid={`save-${provider}`}
            >
              {saving && <Loader2 className="mr-1 h-4 w-4 animate-spin" />}
              Save changes
            </Button>
            <Button
              variant="outline"
              onClick={test}
              disabled={testing || !masked?.configured}
              data-testid={`test-${provider}`}
            >
              {testing && <Loader2 className="mr-1 h-4 w-4 animate-spin" />}
              <ShieldCheck className="mr-1 h-4 w-4" /> Test connection
            </Button>
            {testResult && (
              <span
                className={`text-xs flex items-center gap-1 ${
                  testResult.ok ? "text-emerald-700" : "text-red-700"
                }`}
                data-testid={`test-result-${provider}`}
              >
                {testResult.ok ? (
                  <CheckCircle2 className="h-3.5 w-3.5" />
                ) : (
                  <XCircle className="h-3.5 w-3.5" />
                )}
                {testResult.message}
              </span>
            )}
          </div>
          <p className="text-xs text-stone-500">
            Leave a field blank to keep the existing value. Saved values are
            encrypted with Fernet using <code>KKS_SECRET_ENCRYPTION_KEY</code>.
          </p>
        </div>
      </CardContent>
    </Card>
  );
}

export default function AdminPaymentSettings() {
  const [masked, setMasked] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const data = await adminGetPaymentSettings();
      setMasked(data);
    } catch {
      toast.error("Failed to load payment settings");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const handleSave = async (patch) => {
    const fresh = await adminUpdatePaymentSettings(patch);
    setMasked(fresh);
  };

  const toggleEnabled = async (provider, v) => {
    try {
      const patch =
        provider === "stripe"
          ? { stripe: { enabled: v } }
          : { razorpay: { enabled: v } };
      const fresh = await adminUpdatePaymentSettings(patch);
      setMasked(fresh);
      toast.success(`${provider} ${v ? "enabled" : "disabled"}`);
    } catch {
      toast.error(`Failed to toggle ${provider}`);
    }
  };

  return (
    <div
      className="min-h-screen bg-stone-50 text-stone-900"
      data-testid="admin-payment-settings"
    >
      <DashboardHeader subtitle="Admin · Payment settings" />
      <main className="mx-auto max-w-5xl px-6 py-8 space-y-6">
        <BackButton to="/admin" />
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">
            Payment provider settings
          </h1>
          <p className="text-sm text-stone-500">
            Configure Stripe (USD) and Razorpay (INR). Keys are encrypted at
            rest and never returned in plain text.
          </p>
          <p className="text-xs text-stone-500 mt-2">
            Last updated:{" "}
            <span data-testid="updated-at">
              {masked?.updated_at
                ? new Date(masked.updated_at).toLocaleString()
                : "never"}
            </span>
          </p>
        </div>

        {loading ? (
          <div className="py-10 text-center text-stone-500">
            <Loader2 className="mx-auto h-5 w-5 animate-spin" />
          </div>
        ) : (
          <>
            <ProviderCard
              provider="stripe"
              title="Stripe (USD)"
              description="Cards in USD via Stripe Checkout. Get test keys at dashboard.stripe.com → Developers → API keys."
              masked={masked?.stripe}
              onSave={handleSave}
              onTest={() => adminTestPaymentConnection("stripe")}
              onToggleEnabled={(v) => toggleEnabled("stripe", v)}
            />
            <ProviderCard
              provider="razorpay"
              title="Razorpay (INR)"
              description="UPI / cards / wallets in INR via Razorpay Checkout. Get test keys at dashboard.razorpay.com → Settings → API Keys."
              masked={masked?.razorpay}
              onSave={handleSave}
              onTest={() => adminTestPaymentConnection("razorpay")}
              onToggleEnabled={(v) => toggleEnabled("razorpay", v)}
            />
            <StudyPackDefaultsCard />
            <ReferralDefaultsCard />
          </>
        )}
      </main>
    </div>
  );
}


function StudyPackDefaultsCard() {
  const [defs, setDefs] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    adminGetStudyPackDefaults()
      .then(setDefs)
      .catch(() => setDefs(null));
  }, []);

  if (!defs) {
    return null;
  }

  const onChange = (k, v) => setDefs((d) => ({ ...d, [k]: v }));

  const onSave = async () => {
    setBusy(true);
    try {
      const next = await adminPutStudyPackDefaults({
        default_price_inr: Number(defs.default_price_inr),
        default_price_usd: Number(defs.default_price_usd),
        min_price_inr: Number(defs.min_price_inr),
        min_price_usd: Number(defs.min_price_usd),
      });
      setDefs(next);
      toast.success("Study Pack defaults saved");
    } catch (err) {
      const d = err?.response?.data?.detail || "Failed";
      toast.error(typeof d === "string" ? d : "Failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card className="border-stone-200" data-testid="study-pack-defaults-card">
      <CardHeader>
        <CardTitle className="text-base">Study Pack defaults</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-sm text-stone-500">
          Default prices and floors used when teachers configure Study Pack
          upsells per course. Set floors to 0 to allow teachers to offer the
          Study Pack for free.
        </p>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1">
            <Label>Default price (₹)</Label>
            <Input
              type="number"
              min={0}
              value={defs.default_price_inr}
              onChange={(e) => onChange("default_price_inr", e.target.value)}
              data-testid="sp-default-inr"
            />
          </div>
          <div className="space-y-1">
            <Label>Default price ($)</Label>
            <Input
              type="number"
              min={0}
              step="0.01"
              value={defs.default_price_usd}
              onChange={(e) => onChange("default_price_usd", e.target.value)}
              data-testid="sp-default-usd"
            />
          </div>
          <div className="space-y-1">
            <Label>Min price (₹)</Label>
            <Input
              type="number"
              min={0}
              value={defs.min_price_inr}
              onChange={(e) => onChange("min_price_inr", e.target.value)}
              data-testid="sp-min-inr"
            />
          </div>
          <div className="space-y-1">
            <Label>Min price ($)</Label>
            <Input
              type="number"
              min={0}
              step="0.01"
              value={defs.min_price_usd}
              onChange={(e) => onChange("min_price_usd", e.target.value)}
              data-testid="sp-min-usd"
            />
          </div>
        </div>
        <Button
          onClick={onSave}
          disabled={busy}
          className="bg-stone-900 hover:bg-stone-800 text-white"
          data-testid="sp-save-defaults"
        >
          {busy ? "Saving…" : "Save defaults"}
        </Button>
      </CardContent>
    </Card>
  );
}
